/**
 * 10.9EO — CONEXAO COM O SERVIDOR DA RAID (server/raid). Duas linhas:
 *   boss  -> entra, manda DANO em pacotes (a cada 2s) e recebe a vida global 1x/s + o resultado;
 *   sala  -> manda a propria posicao 4x/s e recebe o RESUMO da sala (ate 40 caçadores).
 * O relogio do servidor manda: cada mensagem traz `now` e daqui sai o ajuste de relogio usado
 * pra roteirizar os golpes do Tita igual em todo celular.
 */
import { GameConfig } from "../config/GameConfig";

export interface RaidJoinInfo {
  room: number; status: string; lobby: number; start: number; end: number;
  hp: number; hpMax: number; seed: number; myDmg: number; verified: boolean; gen?: number; // 10.9GN
}
export interface RaidHpInfo { st: string; hp: number; hpMax: number; online: number; top: { name: string; dmg: number }[]; takenTop?: { name: string; dmg: number }[] }
export interface RaidEndInfo {
  win: boolean; rank: number; myDmg: number; suspect: boolean; total: number; players: number;
  top: { name: string; cls: string; dmg: number }[];
  takenTop?: { name: string; dmg: number }[]; // 10.9GF
  gen?: number; respawnIn?: number; // 10.9GN — geracao + ms ate o boss renascer (0 = nao renasce)
}
export interface RemoteState { id: string; name?: string; cls?: string; x: number; z: number; f: number; hp: number; mhp: number; a: number }

export interface RaidNetHandlers {
  onJoined: (j: RaidJoinInfo) => void;
  onHp: (h: RaidHpInfo) => void;
  onEnd: (e: RaidEndInfo) => void;
  onRoom: (ps: RemoteState[]) => void;
  onLost: (why: string) => void;
}

export class RaidNet {
  private boss: WebSocket | null = null;
  private room: WebSocket | null = null;
  private offset = 0; // servidor - local (ms)
  private pending = 0;
  private pendingTaken = 0; // 10.9GF
  private dmgTimer = 0;
  private closed = false;
  private myId = "";
  private readonly base: string;

  constructor(private readonly slot: string, private readonly h: RaidNetHandlers) {
    this.base = GameConfig.raid.url.replace(/^http/, "ws").replace(/\/$/, "");
  }

  /** Relogio do SERVIDOR (ms). */
  serverNow(): number { return Date.now() + this.offset; }

  private syncClock(now: unknown): void {
    if (typeof now === "number" && Number.isFinite(now)) this.offset = now - Date.now();
  }

  connect(me: { id: string; name: string; cls: string; lvl: number; pd: number }): void {
    this.myId = me.id;
    const ws = new WebSocket(`${this.base}/raid/boss?slot=${encodeURIComponent(this.slot)}`);
    this.boss = ws;
    ws.onopen = () => ws.send(JSON.stringify({ t: "join", ...me }));
    ws.onerror = () => { if (!this.closed) this.h.onLost("erro de conexão"); };
    ws.onclose = () => { if (!this.closed) this.h.onLost("conexão encerrada"); };
    ws.onmessage = (ev) => {
      let m: Record<string, unknown>;
      try { m = JSON.parse(String(ev.data)); } catch { return; }
      this.syncClock(m.now);
      if (m.t === "joined") {
        const j = m as unknown as RaidJoinInfo;
        this.openRoom(me.id, j.room);
        this.h.onJoined(j);
      } else if (m.t === "hp") this.h.onHp(m as unknown as RaidHpInfo);
      else if (m.t === "end") this.h.onEnd(m as unknown as RaidEndInfo);
    };
  }

  private openRoom(myId: string, n: number): void {
    try { this.room?.close(); } catch { /* ignore */ } // 10.9GN — respawn abria 2a sala sem fechar a 1a (onRoom duplicado)
    const rs = new WebSocket(`${this.base}/raid/room?slot=${encodeURIComponent(this.slot)}&n=${n}`);
    this.room = rs;
    rs.onmessage = (ev) => {
      let m: { t?: string; ps?: RemoteState[] };
      try { m = JSON.parse(String(ev.data)); } catch { return; }
      if (m.t === "s" && Array.isArray(m.ps)) this.h.onRoom(m.ps.filter((p) => p && p.id !== myId));
    };
  }

  /** Acumula o dano causado ao Tita; sai em pacote a cada `sendDmgEvery` segundos. */
  addDamage(v: number): void { if (v > 0) this.pending += v; }
  addTaken(v: number): void { if (v > 0) this.pendingTaken += v; } // 10.9GF — dano sofrido

  /** Posicao do MEU caçador (o id e preenchido aqui — a sala guarda o ultimo estado por id). */
  sendPos(s: Omit<RemoteState, "id">): void {
    if (this.room && this.room.readyState === WebSocket.OPEN) this.room.send(JSON.stringify({ t: "p", ...s, id: this.myId }));
  }

  update(dt: number): void {
    this.dmgTimer -= dt;
    if (this.dmgTimer > 0) return;
    this.dmgTimer = GameConfig.raid.sendDmgEvery;
    if (this.boss && this.boss.readyState === WebSocket.OPEN) {
      if (this.pending > 0) { this.boss.send(JSON.stringify({ t: "dmg", v: Math.round(this.pending) })); this.pending = 0; }
      if (this.pendingTaken > 0) { this.boss.send(JSON.stringify({ t: "taken", v: Math.round(this.pendingTaken) })); this.pendingTaken = 0; }
    }
  }

  close(): void {
    this.closed = true;
    try { this.boss?.send(JSON.stringify({ t: "leave" })); } catch { /* fechando */ }
    try { this.boss?.close(); } catch { /* ignore */ }
    try { this.room?.close(); } catch { /* ignore */ }
    this.boss = null;
    this.room = null;
  }
}
